import { LitElement, html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhBellSimple";

import { notificationModalStyles } from "./notification-modal.css";
import "../modal/wow-modal";
import "../notification-card/notification-card";
import type { NotificationCardData } from "../notification-card/notification-card";
import { rpc } from "ui-common/services/rpc";
import { queryClient } from "ui-common/data-query-controller";
import { notificationsQueryKey } from "ui-common/queries/notifications";

@customElement("notification-modal")
export class NotificationModal extends LitElement {
  static override styles = notificationModalStyles;

  @property({ type: Boolean }) open = false;
  @property({ type: Array }) notifications: NotificationCardData[] = [];

  private async _handleClose() {
    const unreadNotifications = this.notifications.filter((n) => !n.isRead);

    if (unreadNotifications.length > 0) {
      try {
        await rpc("mark-all-notifications-read", {});
        await queryClient.invalidateQueries({ queryKey: [notificationsQueryKey] });
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
      }
    }

    this.dispatchEvent(new CustomEvent("close", { bubbles: true, composed: true }));
  }

  private _renderNotificationGroup(notifications: NotificationCardData[], title: string) {
    if (notifications.length === 0) return nothing;

    return html`
      <div class="notification-group">
        <h3 class="group-title">${title}</h3>
        <div class="group-notifications">
          ${notifications.map(
            (notification) => html`<notification-card .notification=${notification}></notification-card>`,
          )}
        </div>
      </div>
    `;
  }

  protected override render() {
    if (!this.open) return nothing;

    const unreadNotifications = this.notifications.filter((n) => !n.isRead);
    const readNotifications = this.notifications.filter((n) => n.isRead);

    return html`
      <wow-modal variant="responsive" mobile-fullscreen mobile-back-arrow @close=${this._handleClose}>
        <span slot="header" class="notifications-header">Notifications</span>
        <div class="notifications-container">
          ${this.notifications.length > 0
            ? html`
                <div class="notifications-list">
                  ${this._renderNotificationGroup(unreadNotifications, "Unread")}
                  ${this._renderNotificationGroup(readNotifications, "Read")}
                </div>
              `
            : html`<div class="empty-state">
                <ph-bell-simple size="48"></ph-bell-simple>
                <p class="group-title">No notifications yet</p>
              </div>`}
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "notification-modal": NotificationModal;
  }
}
